import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tv, Radio, MonitorPlay, Save, UploadCloud, Link as LinkIcon, Calendar, Clock, Users, PlayCircle, Image as ImageIcon, Info } from "lucide-react";
import { toast } from "sonner";
import { MediaProgram } from "@/data/mediaPrograms";
import { Badge } from "@/components/ui/badge";
import PartnerSuggestionPanel from "@/components/PartnerSuggestionPanel";
import { useEmissionsStore } from "@/data/emissionsStore";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MediaEmissionsTrackingPanel } from "@/components/media/MediaEmissionsTrackingPanel";

const MediaDashboardPage = () => {
  const { user } = useAuth();
  
  // Base media setup
  const [channelName, setChannelName] = useState(user?.channelName || "");
  const [mediaTitleFr, setMediaTitleFr] = useState(user?.mediaTitleFr || "");
  const [mediaTitleAr, setMediaTitleAr] = useState(user?.mediaTitleAr || "");
  const [mediaTitleZgh, setMediaTitleZgh] = useState(user?.mediaTitleZgh || "");
  const [mediaType, setMediaType] = useState(user?.mediaType || "tv");
  const [periodicity, setPeriodicity] = useState("hebdo");

  // Tabs and Editing State
  const [activeTab, setActiveTab] = useState("nouvelle");
  const [editingProgramId, setEditingProgramId] = useState<string | null>(null);
  const [originalProgramStatus, setOriginalProgramStatus] = useState<string | null>(null);
  const [showReasonDialog, setShowReasonDialog] = useState(false);
  const [modificationReason, setModificationReason] = useState("");
  const [pendingSaveData, setPendingSaveData] = useState<Partial<MediaProgram> | null>(null);

  // Pretend they are editing the "next" emission for this media account
  const [titleOfDay, setTitleOfDay] = useState("");
  const [subjectAr, setSubjectAr] = useState("");
  const [subjectZgh, setSubjectZgh] = useState("");
  const [description, setDescription] = useState("");
  const [guests, setGuests] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [liveUrl, setLiveUrl] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  
  const [mediaLogoUrl, setMediaLogoUrl] = useState<string | null>(null);
  const [socialYoutube, setSocialYoutube] = useState("");
  const [socialFacebook, setSocialFacebook] = useState("");
  const [socialTiktok, setSocialTiktok] = useState("");
  const [socialInstagram, setSocialInstagram] = useState("");
  const [socialX, setSocialX] = useState("");
  const [socialWebsite, setSocialWebsite] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setPosterUrl(reader.result as string);
        toast.success("Affiche convertie et téléchargée avec succès.");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaLogoUrl(reader.result as string);
        toast.success("Logo généré avec succès.");
      };
      reader.readAsDataURL(file);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEditFromTracking = (prog: MediaProgram) => {
    setEditingProgramId(prog.id);
    setOriginalProgramStatus(prog.status || "pending");
    
    // Fill the form
    setChannelName(prog.channel);
    setMediaTitleFr(prog.frTitle || "");
    setMediaTitleAr(prog.arTitle || "");
    setMediaTitleZgh(prog.zghTitle || "");
    setMediaType(prog.type);
    setPeriodicity(prog.periodicity);
    setTitleOfDay(prog.subject_fr || "");
    setSubjectAr(prog.subject_ar || "");
    setSubjectZgh(prog.subject_zgh || "");
    setDescription(prog.description_fr || "");
    setGuests(prog.guests_fr || "");
    setDate(prog.dateFormatted_fr || "");
    setTime(prog.time || "");
    
    // Calculate end time safely
    if (prog.dateIso && prog.durationMinutes) {
      const d = new Date(prog.dateIso);
      const endD = new Date(d.getTime() + prog.durationMinutes * 60000);
      const endTimeStr = [endD.getHours().toString().padStart(2, "0"), endD.getMinutes().toString().padStart(2, "0")].join(":");
      setEndTime(endTimeStr);
    } else {
      setEndTime("");
    }
    
    setLiveUrl(prog.liveUrl || "");
    setYoutubeUrl(prog.youtubeUrl || "");
    setPosterUrl(prog.posterUrl || null);
    setMediaLogoUrl(prog.mediaLogo || null);
    
    setSocialYoutube(prog.socialLinks?.youtube || "");
    setSocialFacebook(prog.socialLinks?.facebook || "");
    setSocialTiktok(prog.socialLinks?.tiktok || "");
    setSocialInstagram(prog.socialLinks?.instagram || "");
    setSocialX(prog.socialLinks?.x || "");
    setSocialWebsite(prog.socialLinks?.website || "");

    // Switch to form tab
    setActiveTab("nouvelle");
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async (isDraft: boolean = false) => {
    if (!isDraft && !(channelName && (mediaTitleFr || mediaTitleAr || mediaTitleZgh) && date && time && endTime)) {
      toast.error("Veuillez renseigner la chaîne, au moins une langue pour le titre de l'émission, la date, et les heures de début et fin.");
      return;
    }
    
    if (isDraft && !channelName) {
      toast.error("Veuillez au moins renseigner le nom de la chaîne/média pour sauvegarder un brouillon.");
      return;
    }
    
    setIsSubmitting(true);

    const startObj = new Date(`${date}T${time}:00`);
    let endObj = new Date(`${date}T${endTime}:00`);
    if (endObj < startObj) {
      endObj = new Date(endObj.getTime() + 24 * 60 * 60 * 1000);
    }
    const calculatedDuration = Math.max(1, Math.round((endObj.getTime() - startObj.getTime()) / 60000));

    const newProg: Partial<MediaProgram> = {
      id: editingProgramId || undefined,
      frTitle: mediaTitleFr || mediaTitleAr || mediaTitleZgh,
      arTitle: mediaTitleAr || mediaTitleFr,
      zghTitle: mediaTitleZgh || mediaTitleFr,
      subject_fr: titleOfDay,
      subject_ar: subjectAr,
      subject_zgh: subjectZgh,
      channel: channelName,
      type: mediaType as "tv" | "radio" | "web",
      periodicity: periodicity as any,
      dateFormatted_fr: date,
      dateFormatted_ar: date,
      time: time,
      dateIso: startObj.toISOString(),
      durationMinutes: calculatedDuration,
      guests_fr: guests,
      guests_ar: guests,
      guests_zgh: guests,
      description_fr: description,
      description_ar: description,
      description_zgh: description,
      liveUrl: liveUrl,
      youtubeUrl: youtubeUrl,
      posterUrl: posterUrl || undefined,
      mediaLogo: mediaLogoUrl || undefined,
      socialLinks: {
        youtube: socialYoutube || undefined,
        facebook: socialFacebook || undefined,
        tiktok: socialTiktok || undefined,
        instagram: socialInstagram || undefined,
        x: socialX || undefined,
        website: socialWebsite || undefined,
      },
      status: isDraft ? 'draft' : (originalProgramStatus || 'pending'),
      published: originalProgramStatus === 'approved' // keep it published until the backend changes it
    };

    if (!isDraft && originalProgramStatus === 'approved') {
      // Defer saving to show the reason dialog
      setPendingSaveData(newProg);
      setShowReasonDialog(true);
      setIsSubmitting(false); // Fix: Prevent button from being stuck
      return;
    }

    await performSave(newProg);
  };

  const performSave = async (dataToSave: Partial<MediaProgram>, reason?: string) => {
    setIsSubmitting(true);
    try {
      const store = useEmissionsStore.getState();
      const finalData = reason ? { ...dataToSave, modificationReason: reason } : dataToSave;
      const saved = await store.saveEmission(finalData);
      
      if (saved) {
        if (finalData.status === 'draft') {
          toast.success("Votre brouillon a été sauvegardé avec succès !");
        } else {
          toast.success("Les informations de votre émission ont été mises à jour et soumises à validation !");
        }
        
        // Reset only the episode details, not the main channel setup
        setTitleOfDay("");
        setSubjectAr("");
        setSubjectZgh("");
        setDescription("");
        setGuests("");
        setDate("");
        setTime("");
        setEndTime("");
        setLiveUrl("");
        setYoutubeUrl("");
        setPosterUrl(null);
        setEditingProgramId(null);
        setOriginalProgramStatus(null);
        setShowReasonDialog(false);
        setModificationReason("");
        setPendingSaveData(null);
        setActiveTab("suivi"); // Redirect back to tracking to see the pending status
      } else {
        toast.error("Erreur lors de la soumission.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue lors de l'enregistrement.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setTitleOfDay("");
    setSubjectAr("");
    setSubjectZgh("");
    setDescription("");
    setGuests("");
    setDate("");
    setTime("");
    setEndTime("");
    setLiveUrl("");
    setYoutubeUrl("");
    setPosterUrl(null);
    setEditingProgramId(null);
    setOriginalProgramStatus(null);
    setShowReasonDialog(false);
    setModificationReason("");
    setPendingSaveData(null);
    setActiveTab("suivi");
  };

  return (
    <div className="space-y-6 pb-20">
      <div>
        <h1 className="text-3xl font-display font-bold text-foreground">Espace Média</h1>
        <p className="text-muted-foreground font-body">
          Gérez les détails de votre prochaine émission et informez les citoyens.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-muted/50 p-1 rounded-xl w-full sm:w-auto flex flex-col sm:flex-row h-auto gap-2">
          <TabsTrigger value="nouvelle" className="rounded-lg px-6 py-3 font-bold text-base data-[state=active]:bg-primary data-[state=active]:text-white">
            {editingProgramId ? "Modifier l'Émission" : "Nouvelle Émission"}
          </TabsTrigger>
          <TabsTrigger value="suivi" className="rounded-lg px-6 py-3 font-bold text-base data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            Suivi des Émissions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="nouvelle">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
          
          {/* IDENTIFICATION GÉNÉRALE DU PROGRAMME */}
          <Card className="border-border shadow-sm">
            <CardHeader className="bg-muted/30 border-b border-border">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center">
                  <Info className="w-5 h-5 mr-2" /> Identité du Programme
                </CardTitle>
                <Badge variant="outline">Configuration de base</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Type de support</label>
                  <Select value={mediaType} onValueChange={setMediaType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tv">Télévision (TV)</SelectItem>
                      <SelectItem value="radio">Radio</SelectItem>
                      <SelectItem value="web">Web TV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Chaîne / Média *</label>
                  <Input 
                    placeholder="Ex: 2M, Al Aoula, Medi1..." 
                    value={channelName} 
                    onChange={e => setChannelName(e.target.value)} 
                    required
                  />
                </div>
              </div>

              <div className="bg-primary/5 p-3 rounded-lg border border-primary/20 space-y-4">
                <p className="text-xs font-semibold text-primary">Le nom nominal de l'émission doit être dans au moins une langue :</p>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Nom principal (Français)</label>
                  <Input value={mediaTitleFr} onChange={e => setMediaTitleFr(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-foreground">Nom de l'émission (Arabe)</label>
                    <Input value={mediaTitleAr} onChange={e => setMediaTitleAr(e.target.value)} dir="rtl" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-foreground">Nom de l'émission (Tamazight)</label>
                    <Input value={mediaTitleZgh} onChange={e => setMediaTitleZgh(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground">Périodicité</label>
                <Select value={periodicity} onValueChange={setPeriodicity}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quotidien">Quotidien</SelectItem>
                    <SelectItem value="hebdo">Hebdomadaire</SelectItem>
                    <SelectItem value="mensuel">Mensuel</SelectItem>
                    <SelectItem value="ponctuel">Ponctuel (Spécial)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* DÉTAILS PROCHAIN DIRECT */}
          <Card className="border-purple-100 shadow-sm">
            <CardHeader className="bg-purple-50/50 rounded-t-xl border-b border-purple-100">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-purple-900 flex items-center">
                    {mediaType === 'tv' ? <Tv className="w-5 h-5 mr-2" /> : mediaType === 'radio' ? <Radio className="w-5 h-5 mr-2" /> : <MonitorPlay className="w-5 h-5 mr-2" />}
                    Informations du prochain direct
                  </CardTitle>
                  <CardDescription>
                    Préparez le sujet et les horaires de votre prochaine intervention.
                  </CardDescription>
                </div>
                <Badge variant="secondary" className="bg-white text-purple-700 border border-purple-200">Édition en cours</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              
              <div className="bg-muted/10 p-3 rounded-lg border border-border/50 space-y-4">
                <p className="text-xs font-semibold text-muted-foreground">Sujet / Titre de l'épisode du jour (Optionnel) :</p>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground flex items-center">
                    Sujet de l'émission (Français)
                  </label>
                  <Input 
                    placeholder="Ex: Face à Face - Budget 2027" 
                    value={titleOfDay} 
                    onChange={e => setTitleOfDay(e.target.value)} 
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-foreground">Sujet (Arabe)</label>
                    <Input value={subjectAr} onChange={e => setSubjectAr(e.target.value)} dir="rtl" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-foreground">Sujet (Tamazight)</label>
                    <Input value={subjectZgh} onChange={e => setSubjectZgh(e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground flex items-center">
                    <Calendar className="w-4 h-4 mr-1 text-muted-foreground" /> Date de diffusion *
                  </label>
                  <Input 
                    type="date" 
                    value={date} 
                    onChange={e => setDate(e.target.value)} 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground flex items-center">
                    <Clock className="w-4 h-4 mr-1 text-muted-foreground" /> Heure début *
                  </label>
                  <Input 
                    type="time" 
                    value={time} 
                    onChange={e => setTime(e.target.value)} 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground flex items-center">
                    <Clock className="w-4 h-4 mr-1 text-muted-foreground" /> Heure fin *
                  </label>
                  <Input 
                    type="time" 
                    value={endTime} 
                    onChange={e => setEndTime(e.target.value)} 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground flex items-center">
                  <Users className="w-4 h-4 mr-1 text-muted-foreground" /> Invités / Intervenants
                </label>
                <Input 
                  placeholder="Ex: Ministre de la Santé, Répresentants Syndicaux..." 
                  value={guests} 
                  onChange={e => setGuests(e.target.value)} 
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground">
                  Description courte du sujet
                </label>
                <Textarea 
                  placeholder="De quoi allez-vous parler..." 
                  className="resize-none"
                  rows={4}
                  value={description} 
                  onChange={e => setDescription(e.target.value)} 
                />
              </div>

            </CardContent>
          </Card>

          <Card className="border-border shadow-sm">
            <CardHeader className="bg-muted/30">
              <CardTitle className="text-lg flex items-center">
                <LinkIcon className="w-5 h-5 mr-2" /> Liens Externes
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground flex items-center">
                  <LinkIcon className="w-4 h-4 mr-1 text-blue-500" /> Lien de la chaîne ou média (Optionnel)
                </label>
                <Input 
                  placeholder="Ex: Site web, streaming de la chaîne..." 
                  type="url"
                  value={liveUrl} 
                  onChange={e => setLiveUrl(e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground flex items-center">
                  <PlayCircle className="w-4 h-4 mr-1 text-red-600" /> Lien de l'émission / Replay (Optionnel)
                </label>
                <Input 
                  placeholder="https://youtube.com/watch?v=..." 
                  type="url"
                  value={youtubeUrl} 
                  onChange={e => setYoutubeUrl(e.target.value)} 
                />
              </div>

              <div className="space-y-2 mt-4">
                <label className="text-sm font-semibold text-foreground">Réseaux Sociaux et Site Web (Facultatifs)</label>
                <div className="grid grid-cols-2 gap-3 bg-muted/10 p-4 rounded-lg border border-border/50">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Site Web (Direct)</label>
                    <Input value={socialWebsite} onChange={e => setSocialWebsite(e.target.value)} placeholder="https://..." className="h-8 text-xs" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">YouTube</label>
                    <Input value={socialYoutube} onChange={e => setSocialYoutube(e.target.value)} placeholder="https://youtube.com/..." className="h-8 text-xs" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Facebook</label>
                    <Input value={socialFacebook} onChange={e => setSocialFacebook(e.target.value)} placeholder="https://facebook.com/..." className="h-8 text-xs" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Instagram</label>
                    <Input value={socialInstagram} onChange={e => setSocialInstagram(e.target.value)} placeholder="https://instagram.com/..." className="h-8 text-xs" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">TikTok</label>
                    <Input value={socialTiktok} onChange={e => setSocialTiktok(e.target.value)} placeholder="https://tiktok.com/..." className="h-8 text-xs" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">X</label>
                    <Input value={socialX} onChange={e => setSocialX(e.target.value)} placeholder="https://x.com/..." className="h-8 text-xs" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border-border shadow-sm overflow-hidden">
            <CardHeader className="bg-muted/30 border-b border-border pb-4">
              <CardTitle className="text-lg flex items-center">
                <ImageIcon className="w-5 h-5 mr-2" /> Affiche du programme
              </CardTitle>
              <CardDescription>Téléchargez le visuel de l'émission</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {posterUrl ? (
                <div className="relative rounded-lg overflow-hidden border border-border group mb-4">
                  <img src={posterUrl} alt="Affiche" className="w-full h-auto object-cover aspect-video" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                      Changer l'affiche
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 mb-4">
                  <Input 
                    type="url" 
                    placeholder="URL de l'image (ex: https://...)" 
                    value={posterUrl || ""} 
                    onChange={(e) => setPosterUrl(e.target.value)} 
                    className="text-xs h-10"
                  />
                  <div className="flex items-center gap-2">
                    <div className="h-px bg-border flex-1"></div>
                    <span className="text-[10px] text-muted-foreground uppercase font-bold">OU</span>
                    <div className="h-px bg-border flex-1"></div>
                  </div>
                  <div 
                    className="w-full h-20 rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center bg-muted/20 text-muted-foreground hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <UploadCloud className="w-6 h-6 mb-1 opacity-50" />
                    <span className="text-xs font-medium">Uploader depuis l'ordinateur</span>
                  </div>
                </div>
              )}
              
              <input 
                type="file" 
                ref={fileInputRef}
                className="hidden" 
                accept="image/*"
                onChange={handleFileChange}
              />
              <p className="text-xs text-muted-foreground text-center">
                Format recommandé : 16:9 (JPG ou PNG). Max 2 Mo.
              </p>
            </CardContent>
          </Card>

          <Card className="border-border shadow-sm overflow-hidden">
            <CardHeader className="bg-muted/30 border-b border-border pb-4">
              <CardTitle className="text-lg flex items-center">
                <ImageIcon className="w-5 h-5 mr-2" /> Logo Média
              </CardTitle>
              <CardDescription>Uploader ou générer le logo</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {mediaLogoUrl ? (
                <div className="relative rounded-lg overflow-hidden border border-border group mb-4 max-w-[120px] bg-white aspect-square mx-auto">
                  <img src={mediaLogoUrl} alt="Logo" className="w-full h-full object-contain p-2" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button variant="secondary" size="sm" onClick={() => logoInputRef.current?.click()} className="scale-75">
                      Changer
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <Input 
                    type="url" 
                    placeholder="URL du logo (ex: https://...)" 
                    value={mediaLogoUrl || ""} 
                    onChange={(e) => setMediaLogoUrl(e.target.value)} 
                    className="text-xs h-10"
                  />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => handleLogoChange({target: {files: [new File([""], "logo.png")]}} as any)} className="flex-1 text-xs">
                      Générer Auto
                    </Button>
                    <div 
                      className="w-full h-10 flex-1 rounded border-2 border-dashed border-border flex flex-col items-center justify-center bg-muted/20 text-muted-foreground hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => logoInputRef.current?.click()}
                    >
                      <span className="text-xs font-medium text-center">Uploader Logo</span>
                    </div>
                  </div>
                </div>
              )}
              
              <input 
                type="file" 
                ref={logoInputRef}
                className="hidden" 
                accept="image/*"
                onChange={handleLogoChange}
              />
            </CardContent>
          </Card>

          <Card className="border-primary/20 shadow-sm">
            <CardContent className="p-6 bg-primary/5 rounded-xl">
              <div className="flex flex-col sm:flex-row justify-end gap-4">
                {editingProgramId && (
                  <Button onClick={handleCancelEdit} variant="outline" className="flex-1 font-bold">
                    Annuler
                  </Button>
                )}
                <Button onClick={() => handleSave(true)} disabled={isSubmitting} variant="secondary" className="flex-1 font-bold shadow-sm hover:scale-105 transition-transform whitespace-nowrap">
                  <Save className="w-4 h-4 mr-2" />
                  Sauvegarder (Brouillon)
                </Button>
                <Button onClick={() => handleSave(false)} disabled={isSubmitting} className="flex-1 font-bold shadow-md hover:scale-105 transition-transform whitespace-nowrap bg-emerald-600 hover:bg-emerald-700">
                  {isSubmitting ? "Envoi..." : (editingProgramId ? "Modifier et soumettre" : "Soumettre pour validation")}
                </Button>
              </div>
              <p className="text-xs text-center text-muted-foreground mt-4">
                L'émission soumise devra être validée par un administrateur avant d'être publiée. Les brouillons ne sont pas soumis à validation.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
      </TabsContent>

      <TabsContent value="suivi">
        <MediaEmissionsTrackingPanel onEditProgram={handleEditFromTracking} />
      </TabsContent>
      </Tabs>
      
      <Dialog open={showReasonDialog} onOpenChange={(open) => !open && setShowReasonDialog(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Validation des modifications</DialogTitle>
            <DialogDescription>
              Attention : Cette émission est déjà publiée. Toute modification la remettra en statut 'En attente' pour validation par l'administration.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold flex items-center text-red-600">
                <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse mr-2"></span>
                Motif de la modification *
              </label>
              <Textarea 
                placeholder="Expliquez brièvement pourquoi vous modifiez cette émission (ex: Changement d'horaire, nouvel invité...)"
                value={modificationReason}
                onChange={(e) => setModificationReason(e.target.value)}
                className="resize-none"
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Ce motif sera uniquement visible par l'administrateur pour faciliter la validation.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReasonDialog(false)} disabled={isSubmitting}>Annuler</Button>
            <Button 
              onClick={() => {
                if (!modificationReason.trim()) {
                  toast.error("Veuillez indiquer le motif de la modification.");
                  return;
                }
                if (pendingSaveData) performSave(pendingSaveData, modificationReason);
              }} 
              disabled={isSubmitting}
            >
              {isSubmitting ? "Envoi..." : "Envoyer la modification"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-8">
        <PartnerSuggestionPanel partnerType="media" partnerName={channelName || user?.name || "Média"} />
      </div>
    </div>
  );
};

export default MediaDashboardPage;
